import api from '../service/api.service';

const BASE_URL = '/v1/fo/analytics';

/** What kind of element a click was on (ui.clicked). */
export type TrackKind = 'tile' | 'link' | 'button' | 'tab' | 'menu' | 'toggle' | 'other';

/**
 * Small details of an event: at most 10 keys (letters, digits, _), values numbers, booleans,
 * null or strings of at most 100 characters. Never names, emails or anything the user typed.
 */
export type AnalyticsProps = Record<string, string | number | boolean | null>;

/** One usage event. The user and the login session come from the access token on the server. */
export interface AnalyticsEventPayload {
  /** An allowed analytics action, e.g. page.viewed or ui.clicked. */
  action: string;
  /** Route template, e.g. /cases/:caseId/home (never the real URL). */
  page: string;
  previousPage?: string;
  /** The record the event is about, e.g. Case / 123. */
  subjectType?: string;
  subjectId?: string;
  /** ui.clicked: the element's id (see analytics/catalog.ts) and what kind of element it is. */
  target?: string;
  kind?: TrackKind;
  props?: AnalyticsProps;
  /** This browser tab's visit, and the event's position in it. */
  visitId?: string;
  seq?: number;
  /** When it happened, by this browser's clock. */
  clientTime?: string;
}

export interface TrackAnalyticsEventsResponse {
  recorded: number;
  failed: number;
  skipped: number;
}

/**
 * The request body. sentAt (this browser's clock) lets the server measure how far the clock is
 * off and store every event time on its own clock as well.
 */
export const analyticsEventsBody = (events: AnalyticsEventPayload[]) => ({
  events,
  sentAt: new Date().toISOString(),
});

export const postAnalyticsEvents = async (events: AnalyticsEventPayload[]) => {
  return await api.post<TrackAnalyticsEventsResponse>(
    `${BASE_URL}/events`,
    analyticsEventsBody(events),
  );
};

/** Full URL, for the last send when the tab closes (fetch with keepalive, outside axios). */
export const analyticsEventsUrl = (): string =>
  `${process.env.REACT_APP_BASE_API_URL}${BASE_URL}/events`;
